import React, { useCallback } from 'react';
import { IconButton } from '@beeline.com/bp-design-system';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useTenantCode } from 'utils/hooks/useTenantConfig';
import { useIntlFormat } from 'utils/intl/useIntlFormat';
import Drawer from 'common/drawer/Drawer';
import { DrawerContent } from 'common/drawer/DrawerContent';
import DrawerID from 'common/drawer/DrawerID';
import { DrawerTitle } from 'common/drawer/DrawerTitle';
import { RateCardModel } from '@utmost.co/enterprise-models';
import { RatesSummary } from 'src/components/pages/settings/Engagements/RateCards/drawer/RatesSummary';
import { DetailsSummary } from 'src/components/pages/settings/Engagements/RateCards/drawer/DetailsSummary';
import cloneDeep from 'lodash-es/cloneDeep';

interface Props {
  show: boolean;
  onEdit?: () => void;
  onClose: () => void;
  rateCard?: RateCardModel;
}

const RateCardDrawer: React.FC<React.PropsWithChildren<Props>> = ({ rateCard, show, onEdit, onClose }) => {
  const { push } = useHistory();
  const tenantCode = useTenantCode();
  const format = useIntlFormat();

  const handleEdit = useCallback(() => {
    onClose();
    push({ pathname: `/${tenantCode}/frontdoor/add-rate-card`, state: cloneDeep(rateCard) });
    onEdit?.();
  }, [onClose, push, rateCard, tenantCode, onEdit]);

  return (
    <Drawer
      open={show}
      anchor="right"
      variant="temporary"
      onClose={onClose}
      className="timesheet-details"
      dataTest="timesheet-details"
    >
      <DrawerContent
        title={<DrawerID referenceId={rateCard?.referenceId ?? '-'} />}
        onClose={onClose}
        minWidth={564}
        variant="noBorder"
        headerAction={
          <IconButton
            dataTest="edit-drawer-button"
            iconName="edit"
            style={{ padding: 5 }}
            onClick={handleEdit}
            tooltipText={format('settings.engagements.rateCard.edit.action')}
          />
        }
      >
        <DrawerTitle dataTest="drawer-title">{rateCard?.name ?? '-'}</DrawerTitle>
        <StyledSeparator />
        <DetailsSummary rateCard={rateCard} />
        <StyledSeparator />
        <RatesSummary rateCard={rateCard} />
        <StyledSeparator />
      </DrawerContent>
    </Drawer>
  );
};

export default RateCardDrawer;

const StyledSeparator = styled.div`
  height: ${({ theme }) => theme.spacingSize.xxl};
`;
